import { Component, OnInit, TemplateRef, ViewChild, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { MatTable } from '@angular/material/table';
import { MatTabGroup } from '@angular/material/tabs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { ApplicationScreen } from 'src/app/application-navigator/application-screen';
import { StockCheckDetail, InventoryCheckService } from 'src/app/acumatica/EntityServices/StockCheck.service';
import { StockItem, StockItemService } from 'src/app/acumatica/EntityServices/StockItem.service';
import { AlertDialog } from 'src/app/common/alert-dialog/alert-dialog.component';
import { InventorySource, InventorySourceService } from 'src/app/acumatica/EntityServices/OrderPicking.service';
import { Adjustment, AdjustmentDetail, AdjustmentService } from 'src/app/acumatica/EntityServices/Adjustment.service';
import { LoadingIndicator } from 'src/app/acumatica/loading-indicator/AcumaticaLoadingOptions';
import { NotFoundError } from 'src/app/acumatica/acumatica-errors';
import { AudioService, Sound } from 'src/app/services/audio.service';
import { WarehouseService } from 'src/app/acumatica/EntityServices/Warehouse.service';
import { IPrinter, WMSService } from 'src/app/services/wms.service';
import { Transfer, TransferDetail, TransferService } from 'src/app/acumatica/EntityServices/TransferOrder.service';
import { WarehouseLocationInventoryInquiry, WarehouseLocationInventoryInquiryService } from 'src/app/acumatica/EntityServices/WarehouseLocationInventoryInquiry.service';
import { WarehouseSelectionDialogComponent } from 'src/app/common/warehouse-selection-dialog/warehouse-selection-dialog.component';
import { ConfirmationDialog } from 'src/app/common/confirmation-dialog/confirmation-dialog.component';
import { PrinterSelectDialog } from 'src/app/common/printer-selector/printer-selector.component';
import { LogService } from 'src/app/services/logging.service';
import { AcumaticaService } from 'src/app/acumatica/acumatica.service';

import { EditInventoryDialog } from './edit-inventory-dialog/edit-inventory-dialog.component';
import { RestockHeldInventoryComponent } from './restock-held-inventory/restock-held-inventory.component';
import { WarehouseLocationSelectorComponent } from 'src/app/common/warehouse-location-selector/warehouse-location-selector.component';

@Component({
  selector: 'app-stock-check',
  templateUrl: './inventory-check.component.html',
  styleUrls: ['./inventory-check.component.css']
})
export class InventoryCheckComponent extends ApplicationScreen implements OnInit {
  pageTitle = "Inventory Check";

  displayLotHashNbrs = false;

  private _warehouseID!: string;
  warehouseLocations?: string[];
  get warehouseID() { return this._warehouseID; }
  set warehouseID(value: string) {
    this._warehouseID = value;
    this.warehouses.getLocations(value).subscribe(locations => this.warehouseLocations = locations);
  }

  constructor(
    private dialog: MatDialog,
    private router: Router,

    private wms: WMSService,
    private sounds: AudioService,
    private stockCheckService: InventoryCheckService,
    private stockItems: StockItemService,
    private inventoryService: InventorySourceService,
    private adjustmentsService: AdjustmentService,
    private transferService: TransferService,
    private warehouses: WarehouseService,
    private inquiryService: WarehouseLocationInventoryInquiryService,
    private acumatica: AcumaticaService,
    private logger: LogService,
  ) {
    super();
  }

  get isDirty(): boolean { return this.modifiedInventory.length > 0; }
  set isDirty(value: boolean) { }

  screenMenuItems = [
    {
      label: 'Restock Held Items',
      icon: 'trending_up',
      condition: () => this.inventoryInHoldForRestock.length > 0,
      handler: this.doRestockInventoryInHold
    },
    {
      label: 'Update Acumatica',
      icon: 'upgrade',
      handler: this.updateToAcumatica
    },
    {
      label: 'Bin Audit',
      icon: 'shelves',
      condition: isDevMode(),
      handler: this.GetBinLocationToAudit
    },
    {
      label: 'Debug',
      icon: 'adb',
      condition: isDevMode(),
      handler: () => {
        console.warn('inventory', this._inventory);
        console.warn('stockChecks', this.stockCheckRequests);
        console.warn('inventoryInHoldForRestock', this.inventoryInHoldForRestock);
        console.warn('modifiedInventory', this.modifiedInventory);
        console.warn('adjustmentDocument', this.BuildAdjustmentDocument());
      },
    }
  ]

  ngOnInit() {
    if (this.isDevMode) {
      this.warehouseID = '01';
      this.loadStockCheckData();
      return;
    }

    this.dialog.open(WarehouseSelectionDialogComponent).afterClosed().subscribe(warehouseID => {
      if (!warehouseID) { this.router.navigate(['/']); return; }

      this.warehouseID = warehouseID;
      this.loadStockCheckData();
    });
  }

  // #region Stock Check data

  stockCheckRequests: StockCheckDetail[] = [];

  /** fetch data from Acumatica for C.S. requested Stock Check items */
  loadStockCheckData() {
    this.stockCheckService.getStockChecks({ loader: { message: 'Getting Stock Checks', indicator: LoadingIndicator.snackbar } }).subscribe(inquiry => {
      // start the fetch for each unique StockItem object in sequence
      let inventoryIDs = inquiry.Details.map(detail => detail.InventoryID).unique<string>();
      this.getItemsAndInventory(inventoryIDs);

      // sort the initial list of stock check requests and put into data-table bound array
      this.updateStockCheckRequests(inquiry.Details)
    });
  }

  /** sort the stock check requests table as appropriate */
  updateStockCheckRequests(requests: StockCheckDetail[] = this.stockCheckRequests) {
    this.stockCheckRequests = requests.sort((a, b) => {
      if (a.checked != b.checked) return a.checked ? 1 : -1;
      if (a.RequestDate! != b.RequestDate!) return a.RequestDate! < b.RequestDate! ? -1 : 1;
      if (a.InventoryID != b.InventoryID) return a.InventoryID.localeCompare(b.InventoryID);
      if (a.LocationID != b.LocationID) return a.LocationID.localeCompare(b.LocationID);
      if (a.QtyAvailable !== null && b.QtyAvailable !== null && a.QtyAvailable != b.QtyAvailable) return b.QtyAvailable! - a.QtyAvailable!;
      return a.LotSerialNbr.localeCompare(b.LotSerialNbr);
    });
    this.stockCheckRequestsTable.renderRows();

    console.warn(this.stockCheckRequests);
  }

  // #endregion

  // #region Bin Location Audit
  @ViewChild('getBinLocation') GetBinLocationDialog!: TemplateRef<any>;
  @ViewChild('getBinLocationSelector') getBinLocationSelector!: WarehouseLocationSelectorComponent;
  auditBinLocationDialogRef?: MatDialogRef<any>;

  binAuditRequests: StockCheckDetail[] = [];
  BinAuditRollout: boolean = false;
  onBinAuditRollout() { this.getBinLocationSelector.focus(); }

  /** prompt the user for the bin location to audit and start process of content retrieval */
  GetBinLocationToAudit() {
    this.auditBinLocationDialogRef = this.dialog.open(this.GetBinLocationDialog, {
      data: {
        locations: this.warehouseLocations!
      }
    });
    this.auditBinLocationDialogRef.afterClosed().subscribe(response => {
      (<any>this.fieldInventoryID).focus();
      if (response == null) return;

      this.loadBinLocationContentsForAudit(response);
    });
  }

  /** handler for the warehouse-location-selector's locationIDChange (user scanned bin label) event */
  onBinAuditLocationEntered(locationID: string) {
    this.auditBinLocationDialogRef?.close(locationID);
  }

  /** retrieve the contents of the specified bin for auditing */
  loadBinLocationContentsForAudit(locationID: string) {
    this.inquiryService.get(new WarehouseLocationInventoryInquiry({
      WarehouseID: this.warehouseID,
      LocationID: locationID
    }),
      { loader: { message: "Getting Location contents", indicator: LoadingIndicator.snackbar } }
    ).subscribe(response => {
      // create Stock Check Request objects from the Bin Location contents into the Bin Audit array
      this.binAuditRequests = response.Details.map(detail => new StockCheckDetail({
        WarehouseID: this.warehouseID,
        LocationID: locationID,
        InventoryID: detail.InventoryID,
        LotSerialNbr: detail.LotSerialNbr,
      }));

      this.binAuditTable?.renderRows();
      this.tabGroup.selectedIndex = 1;
      this.getItemsAndInventory(this.binAuditRequests.map(req => req.InventoryID)).then(() => {
        this.updateBinAuditTable();
      });
    })

  }

  /** sort the bin audit data table as appropriate */
  updateBinAuditTable() {
    this.binAuditRequests = this.binAuditRequests.sort((a, b) => {
      if (a.checked != b.checked) return a.checked ? 1 : -1;
      if (a.InventoryID != b.InventoryID) return a.InventoryID.localeCompare(b.InventoryID);
      if (a.LocationID != b.LocationID) return a.LocationID.localeCompare(b.LocationID);
      return a.LotSerialNbr.localeCompare(b.LotSerialNbr);
    });
    this.binAuditTable?.renderRows();
  }

  // #endregion

  // #region Item & Inventory data

  @ViewChild('selectLocationForLotSerNbrDialogTemplate') selectLocationForLotSerNbrDialogTemplate!: TemplateRef<any>;

  // dictionary of StockItem retrieval Promises
  _stockItems: { [key: string]: Promise<StockItem> } = {};
  _inventory: { [key: string]: InventorySource[] } = {};

  /** an array of InventorySource records that have adjustments being made to them or location/bin transfers */
  modifiedInventory: InventorySource[] = [];

  /** refresh the tables used in the UI following an adjustment entry */
  updateDisplayTables() {
    this.updateStockCheckRequests();
    this.updateBinAuditTable();
    this.updateModifiedInventory();
  }

  /** refresh the modifiedInventory array with InventorySource records that have either an adjustment or transfer */
  updateModifiedInventory() {
    this.modifiedInventory = Object.values(this._inventory).flat().filter(inv => inv.hasAdjustment || inv.isTransfer);
    this.adjustmentsTable.renderRows();
  }

  /** sequentially fetches the Stock Item and Inventory data for the given list of Inventory IDs */
  getItemsAndInventory(inventoryIDs: string[]): Promise<void> {
    return new Promise<void>(resolve => {
      inventoryIDs = inventoryIDs.unique<string>();
      let idx = -1;
      let getNextItem = () => {
        if (++idx == inventoryIDs.length) { resolve(); return; }
        this.getItemAndInventory(inventoryIDs[idx]).then(getNextItem);
      };
      getNextItem();
    });
  }

  /** returns a promise resolved with the StockItem object of the given Inventory ID after the Stock Item and Inventory data are loaded */
  getItemAndInventory(inventoryID: string): Promise<StockItem> {
    // get the stock item (or return existing promise)
    return this._stockItems[inventoryID] ||= new Promise<StockItem>(resolve => {
      this.stockItems.get(inventoryID, { loader: { message: "Getting " + inventoryID + " item", indicator: LoadingIndicator.snackbar } }).subscribe(item => {
        // get the item inventory data
        this.inventoryService.get(inventoryID, this.warehouseID, null, { loader: { message: "Getting " + inventoryID + " inventory", indicator: LoadingIndicator.snackbar } }).subscribe(inventory => {
          // provide a reference to the StockItem object (to be able to implement quantity rounding)
          inventory.forEach(inv => inv.stockItem = item);

          // add it to the inventory data dictionary
          this._inventory[inventoryID] = inventory;

          // add references to current stock checks / bin audits for the loaded inventory
          this.updateChecksWithInventory(inventory);

          resolve(item);
        });
      }, error => {
        let errorMessage = (error instanceof NotFoundError)
          ? "Invalid Inventory ID\n\"" + inventoryID + "\""
          : "Unrecognized error\n" + error.toString();
        if (error instanceof NotFoundError) this.dialog.open(AlertDialog, {
          data: {
            sound: Sound.ErrorBeep,
            message: errorMessage,
            icon: 'warning',
            buttonText: 'OK'
          }
        }).afterClosed().subscribe(() => this.itemFormReset());
      });
    });
  }
  /** return an array of InventorySource objects for the given Inventory ID*/
  getInventory(inventoryID: string) { return this._inventory[inventoryID]; }

  /** identify any Stock Check or Bin Audit requests for the given InventorySource(s) and assign the Inventory Source reference to them */
  updateChecksWithInventory(inventory: InventorySource): void;
  updateChecksWithInventory(inventory: InventorySource[]): void;
  updateChecksWithInventory(inventory: InventorySource | InventorySource[]): void {
    if (!Array.isArray(inventory)) inventory = [inventory];

    inventory.forEach(source => {
      // update the item in the stock checks list
      let request = this.findInStockChecks(source);
      if (request) request.source = source;

      // update the item in the bin audit list
      request = this.findInBinAudits(source);
      if (request) request.source = source;
    })
  }

  /** return the Stock Check request object for the given inventory source record **/
  findInStockChecks(source: InventorySource): StockCheckDetail | undefined {
    return this.findInStockCheckArray(this.stockCheckRequests, source);
  }
  /** return the Bin Audit (StockCheckDetail) for the given inventory source record */
  findInBinAudits(source: InventorySource): StockCheckDetail | undefined {
    return this.findInStockCheckArray(this.binAuditRequests, source);
  }
  /** return the StockCheckDetail from the given StockCheckDetailArray for the given source piece */
  findInStockCheckArray(details: StockCheckDetail[], source: InventorySource): StockCheckDetail | undefined {
    return details.find((req: StockCheckDetail) => req.InventoryID == source.InventoryID && req.LotSerialNbr == source.LotSerialNbr && req.LocationID == source.OriginalLocationID);
  }

  cancelAdjustment(source: InventorySource) {
    source.AdjustmentQty = undefined;
    this.updateDisplayTables();
  }

  cancelTransfer(source: InventorySource) {
    source.LocationID = source.OriginalLocationID;
    this.updateDisplayTables();
  }

  printStockTag(source: InventorySource): void;
  printStockTag(source: InventorySource, printerID: string): void;
  printStockTag(source: InventorySource, printerID?: string): void {
    if (printerID === undefined) {
      this.dialog.open(PrinterSelectDialog).afterClosed().subscribe((result: { Printer: IPrinter, NumberOfCopies: number }) => {
        if (result.Printer) this.printStockTag(source, result.Printer.id);
      });
      return;
    }

    this.wms.printStockRollTag(
      printerID,
      source.InventoryID,
      source.LotSerialNbr || '',
      source.AdjQtyOnHand,
      undefined,
      undefined,
      1,
      source.hasAdjustment ? 0 : source.CutCount,
      { message: "Printing Stock Tag", indicator: LoadingIndicator.snackbar }
    ).subscribe();
  }

  currentItem: StockItem | null = null;
  // #endregion

  // #region Hold for Restock

  /** inventory items that are currently being held to be restocked */
  inventoryInHoldForRestock: InventorySource[] = [];

  doRestockInventoryInHold() {
    this.dialog.open(RestockHeldInventoryComponent, {
      data: {
        locations: this.warehouseLocations,
        items: this.inventoryInHoldForRestock
      }
    }).afterClosed().subscribe((restockedItems: InventorySource[]) => {
      // remove items that were restocked from the list of items held
      restockedItems.forEach(item => this.inventoryInHoldForRestock.remove(item));
      this.updateDisplayTables();
      this.restockTable?.renderRows();
    })
  }

  // #endregion

  // #region Acumatica Update
  BuildAdjustmentDocument(): Adjustment {
    let adjustmentDocument = new Adjustment();
    adjustmentDocument.Description = "Inventory Check";
    let inventory = this.modifiedInventory.filter(inv => inv.hasAdjustment);
    inventory.forEach(inv => {
      let adjustment = new AdjustmentDetail();
      adjustment.InventoryID = inv.InventoryID;
      adjustment.LotSerialNbr = inv.LotSerialNbr;
      adjustment.WarehouseID = this.warehouseID;
      adjustment.LocationID = inv.OriginalLocationID;
      adjustment.Quantity = inv.AdjustmentAmt;
      adjustment.CommercialMatch = inv.CommercialMatch;
      adjustmentDocument.Details.push(adjustment);
    });

    return adjustmentDocument;
  }

  BuildTransferDocument(): Transfer {
    let transferDocument = new Transfer();
    transferDocument.Description = "Inventory Check"
    transferDocument.FromWarehouseID = this.warehouseID;
    transferDocument.ToWarehouseID = this.warehouseID;
    let inventory = this.modifiedInventory.filter(inv => inv.isTransfer);
    inventory.forEach(inv => {
      let transfer = new TransferDetail();
      transfer.InventoryID = inv.InventoryID;
      transfer.LotSerialNbr = inv.LotSerialNbr;
      transfer.FromLocationID = inv.OriginalLocationID!;
      transfer.ToLocationID = inv.LocationID!;
      transfer.Qty = inv.AdjQtyOnHand;
      transferDocument.Details.push(transfer);
    });

    return transferDocument;
  }

  private _previousUpdateError: any = null;
  updateToAcumatica() {
    if (this.inventoryInHoldForRestock.length > 0) {
      this.doRestockInventoryInHold();
      return;
    }

    if (this._previousUpdateError) {
      this.logger.log(
        this.acumatica.username,
        'Inventory Check - BAD RESUBMIT'
      );
      this.dialog.open(AlertDialog, { data: { message: `Error During\n${this._previousUpdateError.step}\n\nDO NOT RE-SUBMIT -- Contact I.T.` } });
      return;
    }

    interface docs { adjustment: Adjustment | null, transfer: Transfer | null };
    let docs: docs = { adjustment: this.BuildAdjustmentDocument(), transfer: this.BuildTransferDocument() };
    if (docs.adjustment?.Details.length == 0) docs.adjustment = null;
    if (docs.transfer?.Details.length == 0) docs.transfer = null;

    this.logger.log(
      this.acumatica.username,
      'Inventory Check',
      JSON.stringify(docs)
    );

    // // put Adjustments document
    // await (new Promise<void>(resolve => {
    //   if (adjustmentDocument.Details.length == 0) { resolve(); return; }
    //   this.adjustmentsService.put(adjustmentDocument, { loader: "Creating Adjustments" }).subscribe(updatedDoc => {
    //     this.adjustmentsService.Release(updatedDoc, { message: "Creating Adjustments" }).subscribe(() => resolve());
    //   });
    // }));

    // // put Transfers document
    // await (new Promise<void>(resolve => {
    //   if (transferDocument.Details.length == 0) { resolve(); return; }
    //   this.transferService.put(transferDocument, { loader: "Creating Transfers" }).subscribe(updatedDoc => {
    //     this.transferService.Release(updatedDoc, { message: "Creating Transfers" }).subscribe(() => resolve());
    //   });
    // }));

    let createAdjustment = (docs: docs): Promise<docs> => {
      return new Promise<docs>((resolve, reject) => {
        if (!docs.adjustment) { resolve(docs); return; }
        this.adjustmentsService.put(docs.adjustment, { loader: "Creating Adjustments" }).subscribe(savedDoc => resolve({ adjustment: savedDoc, transfer: docs.transfer }), error => reject({ step: 'Create Adjustments', err: error }));
      });
    }

    let releaseAdjustment = (docs: docs): Promise<docs> => {
      return new Promise<docs>((resolve, reject) => {
        if (!docs.adjustment) { resolve(docs); return; }
        this.adjustmentsService.Release(docs.adjustment, { message: "Releasing Adjustments" }).subscribe(() => resolve(docs), error => reject({ step: `Release Adjustment # ${docs.adjustment?.ReferenceNbr}`, err: error }));
      })
    }

    let createTransfer = (docs: docs): Promise<docs> => {
      return new Promise<docs>((resolve, reject) => {
        if (!docs.transfer) { resolve(docs); return; }
        this.transferService.put(docs.transfer, { loader: "Creating Transfer" }).subscribe(savedDoc => resolve({ adjustment: docs.adjustment, transfer: savedDoc }), error => reject({ step: 'Create Transfers', err: error }));
      })
    }

    let releaseTransfer = (docs: docs): Promise<docs> => {
      return new Promise<docs>((resolve, reject) => {
        if (!docs.transfer) { resolve(docs); return; }
        this.transferService.Release(docs.transfer, { message: "Releasing Transfers" }).subscribe(() => resolve(docs), error => reject({ step: `Release Transfer # ${docs.transfer?.ReferenceNbr}`, err: error }));
      })
    }

    createAdjustment(docs)
      .then(docs => createTransfer(docs))
      .then(docs => releaseAdjustment(docs))
      .then(docs => releaseTransfer(docs))
      .then(() => {
        // all successful
        this.dialog.open(AlertDialog, { data: { message: "Stock Checks Updated", buttonText: "OK" } }).afterClosed().subscribe(() => {
          this.modifiedInventory = [];
          this.router.navigate(['/']);
        });
      })
      .catch(err => {
        this.dialog.open(AlertDialog, { data: { message: `An error occurred attempting to ${err.step}.\nDo not re-submit -- contact I.T.` } });
        this._previousUpdateError = err;
        this.modifiedInventory = [];
      });
  }

  // #endregion

  // #region HTML Template UI exposure & element references

  stockCheckColumns = ['Completed', 'InventoryID', 'LocationID', 'Qty', 'Menu'];
  inventoryColumns = ['InventoryID', 'LocationID', 'OnHand', 'menu'];
  restockHoldColumns = ['InventoryID', 'Qty'];

  @ViewChild('adjustmentsTable') adjustmentsTable!: MatTable<InventorySource>;
  @ViewChild('stockCheckRequestsTable') stockCheckRequestsTable!: MatTable<StockCheckDetail>;
  @ViewChild('restockTable') restockTable?: MatTable<InventorySource>;
  @ViewChild('binAuditTable') binAuditTable?: MatTable<InventorySource>;
  @ViewChild('tabGroup') tabGroup!: MatTabGroup;

  // toggleLotDisplay(ctl: HTMLElement) {
  //   let lotHash = ctl.getAttribute('data-lotHash');
  //   let lotNbr = ctl.getAttribute('data-lotNbr');
  //   if (!lotHash) return;
  //   let state = ctl.getAttribute('display-state') ? true : false;
  //   state = !state;
  //   ctl.setAttribute('display-state', state ? 'hash' : '');
  //   ctl.innerHTML = (state ? lotNbr : lotHash) || lotNbr || '';
  // }

  toggleLotDisplay() { this.displayLotHashNbrs = !this.displayLotHashNbrs; }

  // #endregion

  // #region User Data Entry Form

  itemForm = new FormGroup({
    inventoryID: new FormControl(),
    lotSerialNbr: new FormControl()
  });
  get fieldInventoryID() { return this.itemForm.get('inventoryID')!; }
  get fieldLotSerialNbr() { return this.itemForm.get('lotSerialNbr')!; }
  get valueInventoryID() { return this.fieldInventoryID.value; }
  get valueLotSerialNbr() { return this.fieldLotSerialNbr.value; }
  focusInventoryID() { (<any>this.fieldInventoryID).focus(); }
  focusLotSerialNbr() { (<any>this.fieldLotSerialNbr).focus(); }
  resetForm() { this.itemForm.reset(); this.focusInventoryID(); }
  resetInventoryID() { this.fieldInventoryID.setValue(null); this.focusInventoryID(); }
  resetLotSerialNbr() { this.fieldLotSerialNbr.setValue(null); this.focusLotSerialNbr(); }

  itemFormReset() {
    this._getLotSerialNbrResolver = undefined;
    this._getLotSerialNbrValue = undefined;
    this.currentItem = null;
    this.itemForm.reset();
    (<any>this.fieldInventoryID).focus();
  }

  /** event handler for Inventory ID form field being submitted */
  onInventoryIDEntered(inventoryID: string | number | null) {
    if (!inventoryID) return;
    inventoryID = <string>inventoryID;
    this.identifyInventorySource()
      .then(source => this.editInventorySource(source))
      .catch((reason: IdentifyInventorySourceRejectReason) => {
        switch (reason) {
          case IdentifyInventorySourceRejectReason.NotImplemented:
            this.dialog.open(AlertDialog, { data: { sound: Sound.ErrorBeep, icon: 'warning', buttonText: 'OK', message: "Not yet implemented" } });
            break;
          case IdentifyInventorySourceRejectReason.UserCancelled:
            // user cancelled out of dialog -- silently disregard
            break;
          case IdentifyInventorySourceRejectReason.NoInventory:
            this.dialog.open(AlertDialog, { data: { sound: Sound.ErrorBeep, icon: 'warning', buttonText: 'OK', message: "No inventory or \"" + this.valueLotSerialNbr + "\" is not a valid Serial/Lot Number" } })
              .afterClosed().subscribe(() => this.resetLotSerialNbr());
            break;
        }
      });
  }

  // /** contextually handle the user scanning into the given Inventory Source record */
  // processInventorySource(source: InventorySource) {
  //   if (
  //     // is the piece contained in the Stock Check list?
  //     this.findInStockChecks(source)
  //     // or is the piece contained in the Bin Audit list and we're in roll-out mode?
  //     || (this.BinAuditRollout && this.findInBinAudits(source))
  //   ) {
  //     // open the piece editor dialog
  //     this.editInventorySource(source)
  //     return;
  //   }

  //   // the piece scanned is not contained in either list

  // }

  _getLotSerialNbrResolver?: (value: string) => void;
  _getLotSerialNbrValue?: string;
  /** event handler for Lot/Serial Number form field being submitted */
  onLotNumberEntered(lotSerialNbr: string | number | null) {
    if (!lotSerialNbr) return;
    lotSerialNbr = <string>lotSerialNbr;
    // if there's already an open resolver, resolve it with this value
    if (this._getLotSerialNbrResolver) this._getLotSerialNbrResolver(lotSerialNbr);
    // otherwise, store the value to be resolved with when requested
    else this._getLotSerialNbrValue = lotSerialNbr;
  }
  /** returns a promise resolved with the user-entered Lot/Serial Nbr */
  getLotSerialNbr(): Promise<string> {
    // if there's already a stored value, resolve immediately with it
    if (this._getLotSerialNbrValue !== undefined) return Promise.resolve(this._getLotSerialNbrValue);
    // otherwise, return a new promise and store the resolver for when the user enters a value
    else return new Promise<string>(resolve => { this._getLotSerialNbrResolver = resolve; });
  }

  /** identify the InventorySource object indicated by the entry form */
  identifyInventorySource(): Promise<InventorySource> {
    if (this.valueInventoryID === null) return Promise.reject();
    let inventoryID: string = this.valueInventoryID;

    return new Promise<InventorySource>((resolve, reject) => {
      // get the inventory item
      this.getItemAndInventory(inventoryID).then(item => {
        this.currentItem = item;

        // lot/serial items, wait for user to enter the lot/serial number
        if (item.isLotSerItem) this.getLotSerialNbr().then(lotSerialNbr => {
          this.selectSourceLocation(
            this.getInventory(inventoryID).filter(inventory => inventory.LotSerialNbr == lotSerialNbr)
          ).then(resolve).catch(reject);
        });
        // non-lot/serial item
        else {
          this.selectSourceLocation(this.getInventory(inventoryID)).then(resolve).catch(reject);
        }
      });

    });
  }

  /** receives an array of possible inventory sources and returns a Promise resolved with a single one selected by the user or rejected if 0 sources available or user cancels selection */
  selectSourceLocation(sources: InventorySource[]): Promise<InventorySource> {
    if (sources.length == 0) return Promise.reject(IdentifyInventorySourceRejectReason.NoInventory);
    if (sources.length == 1) return Promise.resolve(sources[0]);

    return new Promise<InventorySource>((resolve) => {
      // reject(IdentifyInventorySourceRejectReason.NotImplemented)

      // open a dialog to prompt the user to select the inventory source
      console.warn(sources);
      this.sounds.play(Sound.GentleBeep);
      this.dialog.open(this.selectLocationForLotSerNbrDialogTemplate, { data: { inventory: sources } }).afterClosed().subscribe(resolve);
    });
  }

  /** opens the editor dialog for the given InventorySource object */
  editInventorySource(source: InventorySource) {
    let initAdjustment = source.AdjustmentQty;
    let dialogRef = this.dialog.open(EditInventoryDialog, { data: { source: source, warehouseLocations: this.warehouseLocations } });

    dialogRef.componentInstance.OnPrinterScanned.subscribe(printer => {
      if (printer !== null) this.printStockTag(source, printer);
    });

    dialogRef.afterClosed().subscribe(result => {
      this.itemFormReset();

      switch (result) {
        case 'cancel':
          source.AdjustmentQty = initAdjustment;
          break;

        case 'hold': // for restock
          // this.updateStockCheckWithInventorySource(source);
          this.updateDisplayTables();
          if (!this.inventoryInHoldForRestock.contains(source)) {
            this.inventoryInHoldForRestock.push(source);
            this.restockTable?.renderRows();
          }
          break;

        case 'complete': // immediately restocked
          // this.updateStockCheckWithInventorySource(source);
          this.updateDisplayTables();
          break;
      }
    });
  }

  // #endregion

  // #region Menu Actions
  @ViewChild('stockCheckRequestInfoDialog') stockCheckRequestInfoDialog!: TemplateRef<any>;

  /** open a dialog giving the Stock Check Request info */
  private _requestInfoDialog?: MatDialogRef<any>;
  openRequestInfoDialog(request: StockCheckDetail) {
    this._requestInfoDialog = this.dialog.open(this.stockCheckRequestInfoDialog, { disableClose: false, data: request });
  }
  closeRequestInfoDialog() { this._requestInfoDialog?.close(); }

  /** confirm with user and create Transfer piece to LOST */
  transferToLost(request: StockCheckDetail) {
    this.dialog.open(ConfirmationDialog, {
      data: {
        icon: 'help',
        message: "Transfer piece to LOST location?\n" +
          "Item: " + request.InventoryID + "\n" +
          "Lot: " + request.LotSerialNbr + "\n" +
          "Qty: " + request.source?.QtyOnHand + " " + request.source?.BaseUOM,
        confirmText: 'Yes, It\'s Lost', cancelText: 'No, Cancel'
      }
    }).afterClosed().subscribe(response => {
      if (response == false) return;

      request.source!.LocationID = "LOST";
      request.LocationID = "LOST";
      this.modifiedInventory.push(request.source!);

      this.stockCheckRequestsTable.renderRows();
    });
  }

  /** confirm with user and zero out missing / lost piece from inventory */
  zeroOutPiece(request: StockCheckDetail) {
    this.dialog.open(ConfirmationDialog, {
      data: {
        icon: 'help',
        message: "Are you sure this piece is gone and should be removed from Inventory?\n" +
          "Item: " + request.InventoryID + "\n" +
          "Lot: " + request.LotSerialNbr + "\n" +
          "Qty: " + request.source?.QtyOnHand + " " + request.source?.BaseUOM,
        confirmText: 'Yes, Remove it', cancelText: 'No, Cancel'
      }
    }).afterClosed().subscribe(response => {
      if (response == false) return;

      request.source!.AdjustmentQty = -request.source!.QtyOnHand;
      this.updateDisplayTables();
    });
  }
  // #endregion
}

enum IdentifyInventorySourceRejectReason {
  NotImplemented,
  UserCancelled,
  NoInventory
} 